<div class="card shadow-sm border-0 order-entry-card">
  <div class="card-body p-3 p-lg-4">
    <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
      <div>
        <div class="fw-bold fs-5">{{ title }}</div>
        <div class="text-muted small" *ngIf="isInstantMode">
          Use SwipFx instant liquidity for a locked rate and immediate matching.
        </div>
        <div class="text-muted small" *ngIf="!isInstantMode">
          Set the rate you want and wait for compatible market liquidity.
        </div>
      </div>
    </div>

    <div class="trade-mode-grid mb-4">
      <button type="button" class="trade-mode-card text-start" [class.active]="isInstantMode"
        (click)="selectTradeMode('instant')">
        <div class="d-flex align-items-center justify-content-between gap-2">
          <span class="trade-mode-title">Instant</span>
          <span class="badge text-bg-primary">Fast</span>
        </div>
        <div class="trade-mode-description">
          Accept our SwipFx rate.
        </div>
      </button>

      <button type="button" class="trade-mode-card text-start" [class.active]="!isInstantMode"
        (click)="selectTradeMode('flexible')">
        <div class="d-flex align-items-center justify-content-between gap-2">
          <span class="trade-mode-title">Flexible</span>
          <span class="badge text-bg-light border text-secondary">Your rate</span>
        </div>
        <div class="trade-mode-description">
          Choose your own rate.
        </div>
      </button>
    </div>

    <div *ngIf="blockedMessage" class="alert alert-danger py-2">{{ blockedMessage }}</div>
    <div *ngIf="errorMessage" class="alert alert-danger py-2">
      <div>{{ errorMessage }}</div>
      <button *ngIf="errorButtonText" type="button" class="btn btn-sm btn-outline-danger mt-2"
        (click)="errorAction.emit()">{{ errorButtonText }}</button>
    </div>

    <form [formGroup]="orderForm" (ngSubmit)="submit()" [class.opacity-50]="blockedMessage"
      [attr.aria-busy]="loading || quoteLoading || submissionPending">
      <div class="row g-3 align-items-stretch">
        <div class="col-12 col-lg-5">
          <div class="p-3 rounded-3 bg-light-subtle border border-light h-100">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="fw-semibold">I have</div>
              <span class="badge bg-body text-muted border">Spend</span>
            </div>

            <div class="mb-2">
              <label class="form-label small text-muted mb-1">Currency</label>
              <select class="form-select form-select-sm"
                [ngClass]="{'is-invalid': haveCurrency?.invalid && (haveCurrency!.dirty || haveCurrency!.touched)}"
                formControlName="haveCurrency">
                <option *ngFor="let currency of haveOptions" [value]="currency.currencyCode">
                  {{ currency.currencyCode }} — {{ currency.currencyName }}
                </option>
              </select>
              <div *ngIf="haveCurrency?.invalid && (haveCurrency!.dirty || haveCurrency!.touched)"
                class="invalid-feedback">Please select a currency</div>
            </div>

            <div>
              <label class="form-label small text-muted mb-1">Amount</label>
              <div class="input-group input-group-sm">
                <input type="text" class="form-control"
                  [ngClass]="{'is-invalid': haveAmount?.invalid && (haveAmount!.dirty || haveAmount!.touched)}"
                  placeholder="0.00" inputmode="decimal" formControlName="haveAmount" />
                <span class="input-group-text">{{ haveCurrency?.value }}</span>
              </div>
              <div *ngIf="haveAmount?.invalid && (haveAmount!.dirty || haveAmount!.touched)"
                class="invalid-feedback d-block">
                <div *ngIf="haveAmount?.errors?.['required']">Please enter an amount</div>
                <div *ngIf="haveAmount?.errors?.['pattern']">Please enter a valid amount</div>
                <div *ngIf="haveAmount?.errors?.['min']">Amount is below the minimum allowed</div>
                <div *ngIf="haveAmount?.errors?.['max']">Amount exceeds the available balance or trading limit</div>
              </div>
              <div class="small text-muted mt-2" *ngIf="available !== null">
                Available: <span class="fw-semibold">{{ (available || 0) | smartAmount:haveCurrency?.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-lg-flex col-lg-2 align-items-center justify-content-center">
          <button type="button" class="btn btn-outline-secondary btn-sm rounded-circle swap-btn"
            (click)="swapHaveNeed()" aria-label="Swap currencies">⇄</button>
        </div>

        <div class="d-lg-none col-12 text-center">
          <button type="button" class="btn btn-outline-secondary btn-sm rounded-circle swap-btn"
            (click)="swapHaveNeed()" aria-label="Swap currencies">⇄</button>
          <div class="small text-muted mt-2">Swap</div>
        </div>

        <div class="col-12 col-lg-5">
          <div class="p-3 rounded-3 bg-light-subtle border border-light h-100">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="fw-semibold">I need</div>
              <span class="badge bg-body text-primary border border-primary-subtle">Receive</span>
            </div>

            <div class="mb-2">
              <label class="form-label small text-muted mb-1">Currency</label>
              <select class="form-select form-select-sm"
                [ngClass]="{'is-invalid': needCurrency?.invalid && (needCurrency!.dirty || needCurrency!.touched)}"
                formControlName="needCurrency">
                <option *ngFor="let currency of needOptions" [value]="currency.currencyCode">
                  {{ currency.currencyCode }} — {{ currency.currencyName }}
                </option>
              </select>
              <div *ngIf="needCurrency?.invalid && (needCurrency!.dirty || needCurrency!.touched)"
                class="invalid-feedback">Please select a currency</div>
            </div>

            <div>
              <label class="form-label small text-muted mb-1">Amount</label>
              <div class="input-group input-group-sm">
                <input type="text" class="form-control"
                  [ngClass]="{'is-invalid': needAmount?.invalid && (needAmount!.dirty || needAmount!.touched)}"
                  [readonly]="isInstantMode" placeholder="0.00" inputmode="decimal" formControlName="needAmount" />
                <span class="input-group-text">{{ needCurrency?.value }}</span>
              </div>
              <div *ngIf="needAmount?.invalid && (needAmount!.dirty || needAmount!.touched) && !isInstantMode"
                class="invalid-feedback d-block">
                <div *ngIf="needAmount?.errors?.['required']">Please enter an amount</div>
                <div *ngIf="needAmount?.errors?.['pattern']">Please enter a valid amount</div>
              </div>
              <div class="small text-muted mt-2">
                <ng-container *ngIf="isInstantMode">Calculated from the current SwipFx quote.</ng-container>
                <ng-container *ngIf="!isInstantMode">Editable for your preferred market order.</ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="instant-quote-panel mt-3" *ngIf="isInstantMode" [class.quote-available]="hasUsableInstantQuote"
        [class.quote-unavailable]="!quoteLoading && instantQuote && !instantQuote.instantAvailable">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-2">
          <div>
            <div class="fw-semibold">Instant quote</div>
            <div class="small text-muted">{{ instantQuoteMessage }}</div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge text-bg-success" *ngIf="hasUsableInstantQuote">Available</span>
            <span class="badge text-bg-warning"
              *ngIf="!quoteLoading && instantQuote && !instantQuote.instantAvailable">Unavailable</span>
            <button type="button" class="btn btn-outline-primary btn-sm" *ngIf="enableInstantQuotes"
              [disabled]="quoteLoading || !haveAmount?.value" (click)="refreshInstantQuote()">Refresh</button>
          </div>
        </div>

        <div class="row g-2 mt-2" *ngIf="instantQuote?.offeredRate">
          <div class="col-12 col-md-6">
            <div class="small text-muted">Rate</div>
            <div class="fw-semibold">{{ instantQuote!.offeredRate | number:'1.0-12' }}</div>
          </div>
          <div class="col-12 col-md-6">
            <div class="small text-muted">You receive</div>
            <div class="fw-semibold">{{ (instantQuote!.expectedReceiveAmount || 0) | smartAmount:needCurrency!.value }}
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="small text-muted">Rate protection</div>
          <div class="fw-semibold">Reconfirmed automatically before execution</div>
        </div>

        <div class="mt-3" *ngIf="!quoteLoading && instantQuote && !instantQuote.instantAvailable">
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="selectTradeMode('flexible')">
            Use Flexible instead
          </button>
        </div>
      </div>

      <hr class="my-4">

      <div class="d-flex flex-wrap align-items-end justify-content-between gap-3">
        <div>
          <div class="fw-semibold">Price</div>
          <div class="text-muted small" *ngIf="isInstantMode">Locked by the latest instant quote.</div>
          <div class="text-muted small" *ngIf="!isInstantMode">Quote per base. Editing price recalculates “I need”.
          </div>
        </div>
        <div>
          <label class="form-label small text-muted mb-1">Rate</label>
          <div class="input-group input-group-sm rate-input">
            <input type="text" class="form-control"
              [ngClass]="{'is-invalid': price?.invalid && (price!.dirty || price!.touched)}" [readonly]="isInstantMode"
              placeholder="0.00" inputmode="decimal" formControlName="price" />
            <span class="input-group-text">{{ needCurrency!.value }}/{{ haveCurrency!.value }}</span>
          </div>
          <div *ngIf="price?.invalid && (price!.dirty || price!.touched) && !isInstantMode"
            class="invalid-feedback d-block">
            <div *ngIf="price?.errors?.['required']">Please enter a price</div>
            <div *ngIf="price?.errors?.['pattern']">Please enter a valid price</div>
          </div>
        </div>
      </div>

      <div class="mt-3 p-2 px-3 rounded-3 bg-body border">
        <div class="row g-2 small">
          <div class="col-12 col-md-4 d-flex justify-content-between">
            <span class="text-muted">Est. fee</span>
            <span class="fw-semibold">{{ (+haveAmount!.value * fee / 100) | smartAmount:haveCurrency?.value }}</span>
          </div>
          <div class="col-12 col-md-4 d-flex justify-content-between">
            <span class="text-muted">Total spend</span>
            <span class="fw-semibold">{{ (+haveAmount!.value + (+haveAmount!.value * fee / 100)) |
              smartAmount:haveCurrency?.value }}</span>
          </div>
          <div class="col-12 col-md-4 d-flex justify-content-between">
            <span class="text-muted">Total receive</span>
            <span class="fw-semibold">{{ +needAmount?.value | smartAmount:needCurrency?.value }}</span>
          </div>
        </div>
      </div>

      <div class="mt-3">
        <button class="btn btn-primary w-100 py-2 fw-semibold" [disabled]="!canSubmit" type="submit">
          <span *ngIf="!loading && !quoteLoading && !submissionPending">{{ submitText }}</span>
          <span *ngIf="loading || quoteLoading || submissionPending" class="spinner-border spinner-border-sm"
            aria-hidden="true"></span>
        </button>
        <div class="text-muted small text-center mt-2" *ngIf="isInstantMode && submitBehavior === 'navigate'">
          A fresh quote is validated again on the Orders page before execution.
        </div>
        <div class="text-muted small text-center mt-2" *ngIf="isInstantMode && submitBehavior === 'emit'">
          Better market liquidity is used first; house liquidity fills only the required remainder.
        </div>
        <div class="text-muted small text-center mt-2" *ngIf="!isInstantMode">
          Flexible orders may remain open until matching liquidity becomes available.
        </div>
      </div>
    </form>
  </div>
</div>